import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

/**
 * Admin theme: light, dark or the OS setting (default), stored per browser.
 * Same mechanism as the app (`.dark` on <html>, globals.css tokens); the
 * inline script in index.html applies it before first paint.
 */
export type AdminThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "winche:admin-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function readPreference(): AdminThemePreference {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function subscribeSystem(onChange: () => void): () => void {
  const mq = window.matchMedia(DARK_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

const systemIsDark = () => window.matchMedia(DARK_QUERY).matches;

export function useAdminTheme(): {
  preference: AdminThemePreference;
  resolved: "light" | "dark";
  cycle: () => void;
} {
  const [preference, setPreference] = useState<AdminThemePreference>(readPreference);
  const systemDark = useSyncExternalStore(subscribeSystem, systemIsDark, () => true);
  const resolved: "light" | "dark" = preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    const dark = resolved === "dark";
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.style.colorScheme = dark ? "dark" : "light";
  }, [resolved]);

  const cycle = useCallback(() => {
    setPreference((prev) => {
      const next: AdminThemePreference = prev === "system" ? "light" : prev === "light" ? "dark" : "system";
      try {
        if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // storage blocked: the choice lasts for this tab only
      }
      return next;
    });
  }, []);

  return { preference, resolved, cycle };
}
